// src/pages/club/settings/subscription/steps/ClubStep2.jsx
import "./ClubStep2.scss";

import * as formik from "formik";
import * as yup from "yup";

import { Col, Form, Modal, Row } from "react-bootstrap";
import { useEffect, useMemo, useState } from "react";

import AppButton from "../../../../../commons/buttons/AppButton";
import AppContainer from "../../../../../commons/containers/AppContainer";
import AppCurrencyInputV2 from "../../../../../commons/inputs/AppCurrencyInputV2";
import AppInputV2 from "../../../../../commons/inputs/AppInputV2";
import AppLinkButton from "../../../../../commons/buttons/AppLinkButton";
import AppTextAreaV2 from "../../../../../commons/inputs/AppTextAreaV2";
import CategoryAPI from "../../../../services/CategoryAPI";
import CompanyAPI from "../../../../company/CompanyAPI";
import HallAppToast from "../../../../../commons/HallAppToast";
import { normalizeString } from "../../../../../commons/util/HallAppUtil";

const { Formik } = formik;

const companyAPI = new CompanyAPI();
const categoryAPI = new CategoryAPI();

/**
 * Tipos de benefício
 * - CREDIT: crédito (R$)
 * - DISCOUNT_PERCENT: desconto %
 * - DISCOUNT_AMOUNT: desconto fixo (R$)
 * - INCLUDED_SERVICE: serviço incluso (usa serviceId)
 */
const BENEFIT_TYPES = [
  { value: "CREDIT", label: "Crédito (R$)", hint: "Ex: R$ 100 para usar em serviços" },
  { value: "DISCOUNT_PERCENT", label: "Desconto (%)", hint: "Ex: 10% em qualquer serviço" },
  { value: "DISCOUNT_AMOUNT", label: "Desconto fixo (R$)", hint: "Ex: R$ 20 off em serviços" },
  { value: "INCLUDED_SERVICE", label: "Serviço incluso", hint: "Ex: 1 corte por mês" },
];

/* ===================== BottomSheet (single select) ===================== */

function BottomSheetServicePicker({
  show,
  onClose,
  categories,
  selectedId,
  onConfirm,
}) {
  const [q, setQ] = useState("");
  const [tmp, setTmp] = useState(null);

  useEffect(() => {
    if (show) {
      setQ("");
      setTmp(selectedId || null);
    }
  }, [show, selectedId]);

  const filtered = useMemo(() => {
    const nq = normalizeString(q);
    if (!nq) return categories;

    return (categories || [])
      .map((c) => ({
        ...c,
        services: (c.services || []).filter((s) =>
          normalizeString(`${s.name} ${c.name}`).includes(nq)
        ),
      }))
      .filter((c) => (c.services || []).length);
  }, [q, categories]);

  const pick = (id) => {
    setTmp((prev) => (prev === id ? null : id));
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      fullscreen="md-down"
      centered
      contentClassName="hallapp-modal"
      dialogClassName="bottomsheet-modal"
    >
      <Modal.Header closeButton>
        <div className="w-100">
          <div style={{ fontWeight: 800 }}>Selecionar serviço</div>

          <div className="mt-2">
            <AppInputV2
              label="Pesquisar"
              name="q"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              errors={{}}
              touched={{}}
              placeholder="Nome do serviço…"
            />
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        {(filtered || []).map((cat) => (
          <div key={cat.id} className="mb-3">
            <div style={{ fontSize: 12, fontWeight: 800 }}>{cat.name}</div>

            {(cat.services || []).map((s) => {
              const checked = tmp === s.id;

              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => pick(s.id)}
                  className={`service-pick-row ${checked ? "active" : ""}`}
                >
                  <div className={`service-pick-check ${checked ? "checked" : ""}`}>
                    {checked ? "✓" : ""}
                  </div>
                  <div className="service-pick-name">
                    <strong>{s.name}</strong>
                  </div>
                </button>
              );
            })}
          </div>
        ))}
      </Modal.Body>

      <div className="service-pick-footer">
        <AppLinkButton
          name="Confirmar"
          fullWidth
          onClick={() => {
            onConfirm(tmp);
            onClose();
          }}
        />
      </div>
    </Modal>
  );
}

/* ===================== UI helpers ===================== */

function BenefitTypeCard({ item, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="club-step2__option-card w-100"
      data-active={active ? "1" : "0"}
    >
      <div className="club-step2__option-title">{item.label}</div>
      <div className="club-step2__option-hint">{item.hint}</div>
    </button>
  );
}

function BenefitItem({ benefit, index, onRemove }) {
  const typeLabel =
    BENEFIT_TYPES.find((t) => t.value === benefit.type)?.label || benefit.type;

  const rightPill = (() => {
    if (benefit.type === "CREDIT" || benefit.type === "DISCOUNT_AMOUNT") {
      return `R$ ${Number(benefit.value || 0).toFixed(2)}`;
    }
    if (benefit.type === "DISCOUNT_PERCENT") {
      return `${Number(benefit.value || 0)}%`;
    }
    if (benefit.type === "INCLUDED_SERVICE") {
      return `${benefit.qty || 1}x`;
    }
    return "";
  })();

  return (
    <div className="club-step2__benefit-item">
      <div className="d-flex align-items-start justify-content-between gap-2">
        <div style={{ minWidth: 0 }}>
          <div className="club-step2__benefit-title">
            {benefit.title || `Benefício ${index + 1}`}
          </div>

          <div className="club-step2__benefit-meta text-muted">
            {typeLabel}
            {benefit.type === "INCLUDED_SERVICE" && benefit.serviceName
              ? ` • ${benefit.serviceName}`
              : ""}
          </div>

          {benefit.description ? (
            <div className="club-step2__benefit-desc text-muted">{benefit.description}</div>
          ) : null}
        </div>

        <div className="d-flex align-items-center gap-2">
          {rightPill ? <span className="club-step2__pill">{rightPill}</span> : null}

          <button
            type="button"
            className="club-step2__remove"
            onClick={() => onRemove(index)}
            aria-label="Remover benefício"
            title="Remover"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}

/* ===================== Step 2 ===================== */

function ClubStep2({ nextStep, toBack, data }) {
  const [benefits, setBenefits] = useState(() => data?.benefits || []);
  const [selectedType, setSelectedType] = useState("CREDIT");

  // serviços
  const [categories, setCategories] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  // serviço escolhido (para INCLUDED_SERVICE)
  const [selectedServiceId, setSelectedServiceId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const resp = await categoryAPI.findAllByCompanyScheduler(
          companyAPI.getCompanyManagment().id
        );
        setCategories(resp.data || []);
      } catch {
        setCategories([]);
        HallAppToast.genericError();
      }
    })();
  }, []);

  const serviceLookup = useMemo(() => {
    const map = new Map();
    (categories || []).forEach((c) =>
      (c.services || []).forEach((s) => map.set(s.id, s))
    );
    return map;
  }, [categories]);

  const selectedService = useMemo(() => {
    if (!selectedServiceId) return null;
    return serviceLookup.get(selectedServiceId) || null;
  }, [selectedServiceId, serviceLookup]);

  const initialValues = useMemo(
    () => ({
      title: "",
      description: "",
      value: 0,
      qty: 1,
    }),
    []
  );

  const schema = yup.object().shape({
    title: yup.string().trim().required("Título é obrigatório").min(3, "Mínimo 3 caracteres"),
    description: yup.string().max(240, "Máx. 240 caracteres"),

    // Value obrigatório só em tipos com value
    value: yup.mixed().test("value-required", "Informe o valor", function (v) {
      if (selectedType === "CREDIT" || selectedType === "DISCOUNT_AMOUNT") {
        return Number(v) > 0;
      }
      if (selectedType === "DISCOUNT_PERCENT") {
        const n = Number(v);
        return n > 0 && n <= 100;
      }
      return true; // INCLUDED_SERVICE não usa value
    }),

    qty: yup.mixed().test("qty-required", "Quantidade inválida", function (v) {
      if (selectedType !== "INCLUDED_SERVICE") return true;
      return Number(v) >= 1;
    }),
  });

  const removeBenefit = (idx) => setBenefits((prev) => prev.filter((_, i) => i !== idx));

  const addBenefit = (values, setFieldValue) => {
    if (!values?.title?.trim()) {
      HallAppToast.error("Informe o título do benefício.");
      return;
    }

    // Validação específica do INCLUDED_SERVICE
    if (selectedType === "INCLUDED_SERVICE") {
      if (!selectedServiceId || !selectedService?.id) {
        HallAppToast.error("Selecione o serviço incluso.");
        return;
      }
    }

    const payload = {
      type: selectedType,
      title: values.title.trim(),
      description: (values.description || "").trim() || null,
    };

    if (selectedType === "CREDIT" || selectedType === "DISCOUNT_AMOUNT") {
      const num = Number(values.value || 0);
      if (!num || num <= 0) {
        HallAppToast.error("Informe um valor válido.");
        return;
      }
      payload.value = num;
    }

    if (selectedType === "DISCOUNT_PERCENT") {
      const num = Number(values.value || 0);
      if (!num || num <= 0 || num > 100) {
        HallAppToast.error("Informe um percentual válido (1 a 100).");
        return;
      }
      payload.value = num;
    }

    if (selectedType === "INCLUDED_SERVICE") {
      payload.serviceId = Number(selectedServiceId);
      payload.serviceName = selectedService?.name || null;
      payload.qty = Number(values.qty || 1);
    }

    setBenefits((prev) => [...prev, payload]);

    // reset form (mantém tipo)
    setFieldValue("title", "");
    setFieldValue("description", "");
    setFieldValue("value", 0);
    setFieldValue("qty", 1);
    setSelectedServiceId(null);

    HallAppToast.success("Benefício adicionado!");
  };

  const goNext = () => {
    if (!benefits?.length) {
      HallAppToast.error("Adicione pelo menos um benefício para continuar.");
      return;
    }
    nextStep({ benefits });
  };

  const needsMoney =
    selectedType === "CREDIT" || selectedType === "DISCOUNT_AMOUNT" || selectedType === "DISCOUNT_PERCENT";

  const valueLabel = useMemo(() => {
    if (selectedType === "CREDIT") return "Valor do crédito (R$)";
    if (selectedType === "DISCOUNT_AMOUNT") return "Valor do desconto (R$)";
    if (selectedType === "DISCOUNT_PERCENT") return "Percentual de desconto (%)";
    return "Valor";
  }, [selectedType]);

  const onChangeType = (t, setFieldValue) => {
    setSelectedType(t);
    // reseta seleção de serviço quando sair de INCLUDED_SERVICE
    if (t !== "INCLUDED_SERVICE") setSelectedServiceId(null);
    // reset campos dependentes
    setFieldValue("value", 0);
    setFieldValue("qty", 1);
  };

  return (
    <div className="club-step2" style={{ paddingBottom: 110 }}>
      <div className="mb-3">
        <div className="club-step2__title">Benefícios do Clube</div>
        <div className="club-step2__subtitle text-muted">
          Adicione o que o cliente ganha ao assinar (crédito, desconto ou serviço incluso).
        </div>
      </div>

      {/* Lista atual */}
      <div className="club-step2__card">
        <div className="club-step2__card-title">Benefícios adicionados</div>
        <div className="club-step2__card-hint text-muted">{benefits.length} benefício(s)</div>

        <div className="d-flex flex-column gap-2">
          {benefits.map((b, idx) => (
            <BenefitItem
              key={`${b.type}-${idx}`}
              benefit={b}
              index={idx}
              onRemove={removeBenefit}
            />
          ))}

          {benefits.length === 0 && (
            <div className="club-step2__empty">
              <div className="club-step2__empty-title">Nenhum benefício ainda</div>
              <div className="text-muted" style={{ fontSize: 12 }}>
                Use o formulário abaixo para adicionar o primeiro benefício.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Form adicionar */}
      <div className="club-step2__card">
        <div className="club-step2__card-title">Adicionar benefício</div>
        <div className="club-step2__card-hint text-muted">
          Escolha o tipo e preencha as informações.
        </div>

        <Formik validationSchema={schema} initialValues={initialValues} enableReinitialize>
          {({ handleChange, setFieldValue, values, touched, errors }) => (
            <Form>
              {/* Tipo */}
              <Row className="g-2">
                {BENEFIT_TYPES.map((t) => (
                  <Col xs={6} md={3} key={t.value}>
                    <BenefitTypeCard
                      item={t}
                      active={selectedType === t.value}
                      onClick={() => onChangeType(t.value, setFieldValue)}
                    />
                  </Col>
                ))}
              </Row>

              <hr className="my-3" />

              <Row className="g-2">
                <Col xs={12}>
                  <AppInputV2
                    label="Título"
                    name="title"
                    isRequired
                    errors={errors}
                    touched={touched}
                    value={values.title}
                    placeholder="Ex: Crédito para serviços"
                    onChange={handleChange}
                  />
                </Col>

                <Col xs={12}>
                  <AppTextAreaV2
                    label="Descrição (opcional)"
                    name="description"
                    errors={errors}
                    touched={touched}
                    value={values.description}
                    placeholder="Ex: Pode usar em qualquer serviço do salão"
                    onChange={handleChange}
                  />
                </Col>

                {/* MONEY VALUE */}
                {needsMoney && (
                  <Col xs={12} md={6}>
                    {selectedType === "DISCOUNT_PERCENT" ? (
                      <AppInputV2
                        label={valueLabel}
                        name="value"
                        isRequired
                        errors={errors}
                        touched={touched}
                        value={values.value}
                        placeholder="Ex: 10"
                        onChange={handleChange}
                        type="number"
                      />
                    ) : (
                      <AppCurrencyInputV2
                        label={valueLabel}
                        isRequired
                        value={values.value}
                        onChange={(_, v) => setFieldValue("value", v)}
                      />
                    )}
                    {selectedType === "DISCOUNT_PERCENT" && (
                      <div className="club-step2__hint-mini text-muted">
                        Aceita de 1 a 100.
                      </div>
                    )}
                  </Col>
                )}

                {/* INCLUDED SERVICE */}
                {selectedType === "INCLUDED_SERVICE" && (
                  <>
                    <Col xs={12} md={6}>
                      <div className="club-step2__label">
                        Serviço incluso <span style={{ color: "red" }}>*</span>
                      </div>

                      <button
                        type="button"
                        className="club-step2__service-pick w-100"
                        onClick={() => setPickerOpen(true)}
                      >
                        <div className="club-step2__service-pick-title">
                          {selectedService?.name || "Selecionar serviço"}
                        </div>
                        <div className="club-step2__service-pick-sub text-muted">
                          Toque para escolher no cadastro de serviços
                        </div>
                      </button>

                      {!selectedServiceId && (
                        <div className="club-step2__hint-mini text-muted">
                          Obrigatório para “Serviço incluso”.
                        </div>
                      )}
                    </Col>

                    <Col xs={12} md={6}>
                      <AppInputV2
                        label="Quantidade por ciclo"
                        name="qty"
                        isRequired
                        errors={errors}
                        touched={touched}
                        value={values.qty}
                        placeholder="Ex: 1"
                        onChange={handleChange}
                        type="number"
                      />
                    </Col>
                  </>
                )}
              </Row>

              <div className="mt-3">
                <AppButton
                  name="Adicionar benefício"
                  width="100%"
                  onClick={() => addBenefit(values, setFieldValue)}
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>

      {/* Footer */}
      <div className="club-step2__footer">
        <AppContainer>
          <AppLinkButton name="Próximo" fullWidth={true} onClick={goNext} />
          <div style={{ marginTop: 10 }}>
            <AppButton name="Voltar" width="100%" onClick={toBack} />
          </div>
        </AppContainer>
      </div>

      {/* BottomSheet serviço */}
      <BottomSheetServicePicker
        show={pickerOpen}
        onClose={() => setPickerOpen(false)}
        categories={categories}
        selectedId={selectedServiceId}
        onConfirm={(id) => setSelectedServiceId(id || null)}
      />
    </div>
  );
}

export default ClubStep2;
